import { HelpHint } from "../misc/HelpHint";
import { ReferenceField } from "@/components/admin/reference-field";
import {
  useCreate,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageComposer } from "../notes/MessageComposer";
import { NoteAttachments } from "../notes/NoteAttachments";
import { Badge } from "@/components/ui/badge";
import { ReviewExpense } from "../finance/ProjectExpenses";
import { ActivityAuthor } from "../activity/ActivityAuthor";
import type { FinancePlan } from "../finance/financeModel";
import type { Deal, Expense, Task, AttachmentNote } from "../types";
import { BUDGET_ZONES, type BudgetZone } from "../finance/adjustmentModel";
import { FINANCIAL_ZONES, type ZoneKey } from "../finance/financeModel";
import {
  EXPENSE_CATEGORIES,
  EXPENSE_REASONS,
  expenseCategoryLabel,
  rubles,
  type ExpenseReason,
} from "../finance/expenseModel";
const statuses = {
  pending: "На проверке",
  approved: "Подтверждён",
  rejected: "Отклонён",
};
export const TaskExpenses = ({ taskId }: { taskId: string | number }) => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const { data: task, isPending: taskLoading } = useGetOne<Task>("tasks", {
    id: taskId,
  });
  const {
    data: expenses = [],
    isPending,
    error,
    refetch,
  } = useGetList<Expense>("expenses", {
    filter: { task_id: taskId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });
  const isAdmin = !!identity?.administrator;
  const { data: project } = useGetOne<Deal>(
    "deals",
    { id: task?.deal_id ?? -1 },
    { enabled: isAdmin && task?.deal_id != null },
  );
  const { data: plans } = useGetList<FinancePlan>(
    "project_finance_plans",
    {
      filter: { deal_id: task?.deal_id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "revision", order: "DESC" },
    },
    { enabled: isAdmin && task?.deal_id != null },
  );
  const [create, { isPending: isCreating }] = useCreate();
  const [files, setFiles] = useState<AttachmentNote[]>([]);
  const [category, setCategory] = useState("delivery"),
    [amount, setAmount] = useState(""),
    [comment, setComment] = useState("");
  const [zone, setZone] = useState<ZoneKey>("administration"),
    [reason, setReason] = useState<ExpenseReason>("planned");
  const [to, setTo] = useState<BudgetZone>("reserve");
  const [spentAt, setSpentAt] = useState(() => {
    const d = new Date();
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-");
  });
  const handleAdd = () => {
    if (!identity || task?.deal_id == null) return;
    const n = Number(amount);
    if (
      !Number.isFinite(n) ||
      n <= 0 ||
      n >= 10000000000 ||
      Math.abs(n * 100 - Math.round(n * 100)) > 0.000001
    ) {
      notify("Укажите положительную сумму, до двух знаков после запятой", {
        type: "warning",
      });
      return;
    }
    if (!comment.trim() || !spentAt) {
      notify("Укажите дату и пояснение расхода", { type: "warning" });
      return;
    }
    create(
      "expenses",
      {
        data: {
          sales_id: identity.id,
          created_by_sales_id: identity.id,
          task_id: taskId,
          deal_id: task.deal_id,
          category,
          amount: n,
          comment: comment.trim(),
          attachments: files,
          spent_at: spentAt,
          status: "pending",
          proposed_zone: isAdmin ? zone : null,
          expense_reason: isAdmin ? reason : null,
          proposed_to_zone: isAdmin && reason !== "planned" ? to : null,
        },
      },
      {
        onSuccess: async () => {
          setAmount("");
          setComment("");
          setFiles([]);
          await refetch();
          notify("Заявка отправлена администратору", { type: "success" });
        },
        onError: () =>
          notify(
            "Не удалось отправить заявку. Проверьте проект и назначение задачи.",
            { type: "error" },
          ),
      },
    );
  };
  return (
    <div className="space-y-4 border-t pt-4">
      <div>
        <div className="flex items-center gap-1">
          <h3 className="font-medium">Расходы задачи</h3>
          <HelpHint>
            Заявки учитываются в финансах после подтверждения администратором.
          </HelpHint>
        </div>
      </div>
      {isPending ? (
        <p>Загрузка заявок…</p>
      ) : error ? (
        <p role="alert">Не удалось загрузить расходы</p>
      ) : expenses.length === 0 ? (
        <p className="text-sm text-muted-foreground">Заявок пока нет.</p>
      ) : (
        expenses.map((e) =>
          isAdmin && e.status === "pending" && project ? (
            <ReviewExpense
              key={e.id}
              expense={e}
              project={project}
              plan={plans?.[0]}
            />
          ) : (
            <div key={e.id} className="rounded-lg border p-3 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">
                  {expenseCategoryLabel(e.category)}
                </span>
                <span>{rubles(e.amount)}</span>
              </div>
              <Badge variant="outline">{statuses[e.status]}</Badge>
              <p className="text-xs mt-2">
                Проект расхода:{" "}
                {e.deal_id != null ? (
                  <ReferenceField
                    record={e}
                    source="deal_id"
                    reference="project_lookup"
                    link={false}
                    render={({ referenceRecord }) =>
                      referenceRecord?.name ?? "Проект недоступен"
                    }
                  />
                ) : (
                  "Не назначен"
                )}
              </p>
              <p className="mt-2 whitespace-pre-line">{e.comment}</p>
              <NoteAttachments note={e} />
              <p className="text-xs text-muted-foreground mt-1">
                <ActivityAuthor id={e.created_by_sales_id} /> · {e.spent_at} ·
                Зона:{" "}
                {FINANCIAL_ZONES.find((z) => z.key === e.proposed_zone)
                  ?.label ?? "Не указана"}
              </p>
            </div>
          ),
        )
      )}
      {taskLoading ? (
        <p>Загрузка задачи…</p>
      ) : task?.deal_id == null ? (
        <p className="rounded-lg bg-muted p-3 text-sm">
          Для подачи расхода сначала укажите проект в задаче.
        </p>
      ) : (
        <fieldset
          disabled={isCreating}
          className="rounded-lg border p-4 space-y-3"
        >
          <legend className="text-sm font-medium">
            Подать расход на проверку
          </legend>
          <label className="block text-sm">
            Категория
            <select
              className="w-full h-9 border rounded-md px-2 bg-background"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {EXPENSE_CATEGORIES.filter((c) => c.value !== "salary").map(
                (c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ),
              )}
            </select>
          </label>
          {isAdmin && (
            <>
              <label className="block text-sm">
                Зона списания
                <select
                  className="w-full h-9 border rounded-md px-2 bg-background"
                  value={zone}
                  onChange={(e) => {
                    const next = e.target.value as ZoneKey;
                    setZone(next);
                    if (to === next) setTo("reserve");
                  }}
                >
                  {FINANCIAL_ZONES.map((z) => (
                    <option key={z.key} value={z.key}>
                      {z.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                Причина
                <select
                  className="w-full h-9 border rounded-md px-2 bg-background"
                  value={reason}
                  onChange={(e) => setReason(e.target.value as ExpenseReason)}
                >
                  {EXPENSE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              {reason !== "planned" && (
                <label className="block text-sm">
                  Предложенный бюджет-получатель
                  <select
                    className="w-full h-9 border rounded-md px-2 bg-background"
                    value={to}
                    onChange={(e) => setTo(e.target.value as BudgetZone)}
                  >
                    {BUDGET_ZONES.filter((z) => z.key !== zone).map((z) => (
                      <option key={z.key} value={z.key}>
                        {z.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              Сумма, ₽
              <Input
                type="number"
                min="0.01"
                max="9999999999.99"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
            <label className="text-sm">
              Дата расхода
              <Input
                type="date"
                value={spentAt}
                onChange={(e) => setSpentAt(e.target.value)}
              />
            </label>
          </div>
          <div className="space-y-1 text-sm">
            <label htmlFor="expense-comment">Пояснение расхода</label>
            <MessageComposer
              id="expense-comment"
              value={comment}
              onChange={setComment}
              files={files}
              onFilesChange={setFiles}
              disabled={isCreating}
              placeholder="Что оплачено? Можно приложить чек или скриншот"
            />
          </div>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={isCreating || !amount || !comment.trim() || !spentAt}
          >
            {isCreating ? "Отправка…" : "Отправить на проверку"}
          </Button>
        </fieldset>
      )}
    </div>
  );
};
